import { useState, useMemo } from 'react'
import { Check, Search } from 'lucide-react'
import { FIAT_CURRENCIES } from '@parse/shared'
import { Modal } from '@/components/Modal'
import { useThemeColors } from '@/hooks/useThemeColors'
import { FONT_BODY } from '@/config/theme'

interface FiatCurrencyModalProps {
  show: boolean
  onClose: () => void
  selected: string
  onSelect: (currency: string) => void
}

/**
 * #134 — re-exported from @parse/shared, which is the SINGLE source of truth shared with the
 * backend's `VS_CURRENCIES`. This was a hand-maintained copy that had to stay "matching" the
 * backend list by comment; drift would have made this picker offer a currency we never fetched a
 * rate for, and the fallback then prints that currency's SYMBOL against an UNCONVERTED USD amount.
 * Add or remove currencies in `packages/shared/src/fiat-currencies.ts`, never here.
 */
export const CURRENCIES = FIAT_CURRENCIES

export function FiatCurrencyModal({
  show,
  onClose,
  selected,
  onSelect,
}: FiatCurrencyModalProps) {
  const c = useThemeColors()
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!search) return CURRENCIES
    const q = search.toLowerCase()
    return CURRENCIES.filter(
      cur => cur.code.toLowerCase().includes(q) || cur.name.toLowerCase().includes(q)
    )
  }, [search])

  const handleSelect = (code: string) => {
    onSelect(code.toLowerCase())
    localStorage.setItem('parsebridge:fiatCurrency', code.toLowerCase())
    setSearch('')
    onClose()
  }

  return (
    <Modal
      show={show}
      onClose={() => { setSearch(''); onClose() }}
      title="Display Currency"
    >
      <div style={{ padding: 16, fontFamily: FONT_BODY }}>
        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <Search
            size={16}
            style={{
              position: 'absolute', left: 12,
              top: '50%', transform: 'translateY(-50%)',
              color: c.mu,
            }}
          />
          <input
            type="text"
            placeholder="Search currency..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
            style={{
              width: '100%', paddingLeft: 40, paddingRight: 16,
              paddingTop: 10, paddingBottom: 10,
              background: c.blB, border: `1px solid ${c.dv}`,
              borderRadius: 12, fontSize: 14,
              color: c.tx, outline: 'none',
              fontFamily: FONT_BODY, boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Currency list */}
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {filtered.map(cur => {
            const isSelected = cur.code.toLowerCase() === selected.toLowerCase()
            return (
              <button
                key={cur.code}
                onClick={() => handleSelect(cur.code)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', padding: '12px 14px',
                  background: isSelected ? c.aB : 'transparent',
                  border: 'none', borderBottom: `1px solid ${c.dv}`,
                  cursor: 'pointer', transition: 'background 0.15s',
                  fontFamily: FONT_BODY,
                }}
                onMouseEnter={e => {
                  if (!isSelected) (e.currentTarget as HTMLButtonElement).style.background = c.blB
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLButtonElement).style.background = isSelected ? c.aB : 'transparent'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: c.blB, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, fontWeight: 600, color: c.ac,
                  }}>
                    {cur.symbol}
                  </span>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: c.tx }}>
                      {cur.code}
                    </div>
                    <div style={{ fontSize: 12, color: c.mu }}>
                      {cur.name}
                    </div>
                  </div>
                </div>
                {isSelected && <Check size={16} color={c.gr} />}
              </button>
            )
          })}
        </div>
      </div>
    </Modal>
  )
}
